import { useEffect, useRef, useState } from 'react';
import { useCdrDetail } from '../../lib/api/hooks.js';
import { api } from '../../lib/api/client.js';
import { Badge, ErrorBanner } from '../../components/ui/Primitives.js';
import { toastError } from '../../components/ui/Toast.js';
import { Icon } from '../../components/ui/Icon.js';
import { formatDuration, formatMoney, formatDateTime } from '../../lib/format.js';

/**
 * One call, opened from its row in the history.
 *
 * The questions that follow "there it is" are all answered here and none of them
 * in the list: why it ended that way, what the network did to it, what it was
 * rated against, what the other legs did, and what it sounded like. The list had
 * no detail view at all -- the rows did not open -- which is what was reported.
 *
 * Grouped by the question rather than by column order, and every group is omitted
 * when it has nothing to say. A panel of eighteen dashes reads as a broken screen;
 * a panel with three facts reads as a call that has three facts.
 */
export function CallDetail({ tenantId, callId, tz }: {
  tenantId: string; callId: string; tz: string;
}) {
  const q = useCdrDetail(tenantId, callId);
  const [more, setMore] = useState(false);

  if (q.error) return <ErrorBanner error={q.error} />;
  if (!q.data) {
    return <span className="arc-subtle" style={{ fontSize: 12 }}>Loading the call…</span>;
  }

  const c = q.data;
  const num = (v: string | null, unit: string): string | null =>
    v === null ? null : `${Number(v).toFixed(unit === 'ms' ? 1 : 2)} ${unit}`;

  /**
   * When, who and what it cost: what most calls are opened for. The rest waits
   * behind More info, because all of it at once was too much to read (owner,
   * 15 September 2026). A rating the node and platform disagree on is a problem,
   * so it stays up here.
   */
  const summary: Group[] = [
    ['Call', [
      ['Started', formatDateTime(c.started_at, tz)],
      ['Answered', c.answered_at ? formatDateTime(c.answered_at, tz) : 'never answered'],
      ['Ended', formatDateTime(c.ended_at, tz)],
      ['Talk time', c.answered_at ? formatDuration(c.duration_sec) : null],
      ['Charged', c.sell_amount === null ? null : formatMoney(c.sell_amount, c.currency)],
      // Rated twice and disagreed: the node rated it during the call and the
      // platform rated it again on arrival. Worth seeing on the call itself.
      ['Rating', c.rating_mismatch
        ? <Badge tone="warn">node and platform disagreed</Badge>
        : null],
    ]],
    ['Parties', [
      // A caller with no name comes through with the number as the name.
      ['From', c.from_name && c.from_name !== c.from_number
        ? `${c.from_name} · ${c.from_number ?? ''}` : c.from_number],
      ['To', c.to_number],
      ['Extension', c.extension_number],
    ]],
  ];

  const details: Group[] = [
    ['Signalling', [
      // The cause is the answer to "why did this call do that", and the SIP code
      // is what a carrier will quote back at you about it.
      ['Hangup cause', c.hangup_cause ? <span className="arc-mono">{c.hangup_cause}</span> : null],
      ['SIP response', c.sip_code ? <span className="arc-mono">{c.sip_code}</span> : null],
      ['Dialled', c.destination_e164 !== c.to_number ? c.destination_e164 : null],
      ['Carrier', c.trunk_name ?? null],
    ]],
    ['Network', [
      ['Codec', c.codec ? <span className="arc-mono">{c.codec}</span> : null],
      ['MOS', c.mos],
      ['Jitter', num(c.jitter_ms, 'ms')],
      ['Packet loss', c.packet_loss_pct === null ? null : `${Number(c.packet_loss_pct).toFixed(2)} %`],
      ['Round trip', num(c.rtt_ms, 'ms')],
    ]],
    ['Billing', [
      ['Billable', c.is_billable_leg ? formatDuration(c.billable_sec)
        : c.on_own_trunk && c.leg === 'a' ? 'not charged, own trunk' : 'not the billable leg'],
      ['Cost', c.cost_amount === undefined || c.cost_amount === null
        ? null : formatMoney(c.cost_amount, c.currency)],
      ['Margin', c.margin_amount === undefined || c.margin_amount === null
        ? null : formatMoney(c.margin_amount, c.currency)],
      ['Rated on node', c.rated_on_node === undefined || c.rated_on_node === null
        ? null : formatMoney(c.rated_on_node, c.currency)],
      ['Rate rule', c.rate_rule_id ? <span className="arc-mono">{c.rate_rule_id.slice(0, 8)}</span> : null],
    ]],
  ];

  return (
    <div className="arc-stack" style={{ gap: 16 }}>
      <Groups groups={summary} />

      {c.has_recording && (
        <Recording tenantId={tenantId} callId={c.id} status={c.recording_status ?? 'ready'} />
      )}

      <div>
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-expanded={more}
          onClick={() => setMore((v) => !v)}>
          <Icon name={more ? 'chevronUp' : 'chevronDown'} size={13} />
          {more ? 'Less info' : 'More info'}
        </button>
      </div>

      {more && (
        <>
          <Groups groups={details} />

          {/*
            Only when there IS another leg. An outbound call is the seat's leg and
            the carrier's, and which of the two the list showed is an accident of
            sorting -- so the whole call is shown, with this row marked.
          */}
          {c.legs.length > 1 && (
            <div>
              <div className="arc-label" style={{ marginBottom: 6 }}>
                Legs of this call
              </div>
              <div className="arc-table-wrap">
                <table className="arc-table">
                  <thead>
                    <tr>
                      <th>Leg</th><th>Direction</th><th>From</th><th>To</th>
                      <th className="arc-td-right">Duration</th><th>Ended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.legs.map((l) => (
                      <tr key={l.id}>
                        <td>
                          {l.leg === 'a' ? 'caller' : 'carrier'}
                          {l.id === c.id && <span className="arc-subtle"> · shown above</span>}
                        </td>
                        <td>{l.direction}</td>
                        <td className="arc-mono">{l.from_number ?? '—'}</td>
                        <td className="arc-mono">{l.to_number ?? '—'}</td>
                        <td className="arc-td-right arc-num">{formatDuration(l.duration_sec)}</td>
                        <td className="arc-mono arc-subtle">
                          {l.hangup_cause ?? '—'}{l.sip_code ? ` · ${l.sip_code}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Staff only, and collapsed again: it is the switch's own plumbing,
              useful on the day a call went wrong and noise on every other day. */}
          {c.variables && Object.keys(c.variables).length > 0 && (
            <details>
              <summary className="arc-subtle" style={{ fontSize: 12, cursor: 'pointer' }}>
                Channel variables ({Object.keys(c.variables).length})
              </summary>
              <pre className="arc-mono" style={{
                margin: '8px 0 0', padding: 10, fontSize: 11.5, lineHeight: 1.5,
                background: 'var(--arc-surface-3)', borderRadius: 'var(--arc-r-sm)',
                maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>{JSON.stringify(c.variables, null, 2)}</pre>
            </details>
          )}
        </>
      )}
    </div>
  );
}

type Row = [string, React.ReactNode];
type Group = [string, Row[]];

/** Rows with nothing in them are dropped, not rendered as a dash, and so is a group left empty. */
function Groups({ groups }: { groups: Group[] }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16,
    }}>
      {groups.map(([title, rows]) => {
        const present = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
        if (present.length === 0) return null;
        return (
          <div key={title}>
            <div className="arc-label" style={{ marginBottom: 6 }}>{title}</div>
            <dl className="arc-kv">
              {present.map(([k, v]) => (
                <div key={k} style={{ display: 'contents' }}>
                  <dt>{k}</dt><dd>{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The recording, played in place (docs/26 E5).
 *
 * Pressing Play asks the API for a link: a path on our own domain that names
 * this call, lasts five minutes and only works in this browser. The player
 * streams it, so a long call starts at once and seeks, and no link to a
 * recording sits in the page before anybody asked to hear it.
 *
 * Saving and unavailable are NAMED states, not dead buttons. A recording is
 * saving until its node reports it in storage, usually seconds after the call;
 * unavailable means it never arrived, or has passed its retention.
 */
const RECORDING_BASE = '/api/v1/recordings/';

function Recording({ tenantId, callId, status }: {
  tenantId: string; callId: string; status: 'ready' | 'saving' | 'unavailable';
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Where a refreshed link picks up from, and when the last refresh happened,
  // so a link that keeps failing cannot loop.
  const resumeAt = useRef(0);
  const refreshedAt = useRef(0);

  const link = async (download: boolean): Promise<string | null> => {
    try {
      const r = await api.post<{ token: string }>('/v1/recordings/link',
        { tenant_id: tenantId, cdr_id: callId, download });
      return RECORDING_BASE + r.token;
    } catch (e) {
      toastError(e);
      return null;
    }
  };

  const play = async (): Promise<void> => {
    setBusy(true); setError(null);
    const url = await link(false);
    setBusy(false);
    if (url) setSrc(url);
  };

  /**
   * A seek after the link has expired fails. Ask for a new one and carry on
   * from the same place; a second failure within half a minute is shown instead.
   */
  const recover = async (): Promise<void> => {
    const a = audioRef.current;
    if (!a || Date.now() - refreshedAt.current < 30_000) {
      setError('The recording stopped loading.');
      return;
    }
    refreshedAt.current = Date.now();
    resumeAt.current = a.currentTime;
    const url = await link(false);
    if (url) setSrc(url);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src || resumeAt.current === 0) return;
    const resume = () => { a.currentTime = resumeAt.current; resumeAt.current = 0; void a.play(); };
    a.addEventListener('loadedmetadata', resume, { once: true });
    return () => a.removeEventListener('loadedmetadata', resume);
  }, [src]);

  const download = async (): Promise<void> => {
    setBusy(true); setError(null);
    const url = await link(true);
    setBusy(false);
    if (!url) return;
    // Same origin, and the response says attachment, so the browser saves it
    // without leaving the page.
    const a = document.createElement('a');
    a.href = url;
    a.click();
  };

  if (status !== 'ready') {
    return (
      <div>
        <div className="arc-label" style={{ marginBottom: 6 }}>Recording</div>
        <p className="arc-subtle" style={{ margin: 0 }}>
          {status === 'saving'
            ? 'Being saved to storage. It can be played in a minute or two.'
            : 'Recorded, but the file is not in storage.'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="arc-label" style={{ marginBottom: 6 }}>Recording</div>
      <div className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {src ? (
          <audio ref={audioRef} src={src} controls autoPlay preload="auto"
            style={{ width: '100%', maxWidth: 420 }} onError={() => void recover()} />
        ) : (
          <button type="button" className="arc-btn arc-btn-sm" disabled={busy}
            onClick={() => void play()}>
            <Icon name="play" size={13} />
            {busy ? 'Loading…' : 'Play'}
          </button>
        )}
        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={busy}
          onClick={() => void download()}>
          Download
        </button>
      </div>
      {error && <p className="arc-error" style={{ marginTop: 6 }}>{error}</p>}
    </div>
  );
}
